import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter, faSpinner, faRotateLeft, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
import { useDocumentViewer } from '../contexts/DocumentViewerContext'
import {
  auditService,
  type FormCompletionQuery,
  type FormCompletionRow,
} from '../services/auditService'
import SuggestInput, { resolveSuggestValue, type SuggestOption } from './SuggestInput'

type Props = {
  fromTs: string
  toTs: string
  limit: number
  onFromTsChange: (v: string) => void
  onToTsChange: (v: string) => void
  onLimitChange: (n: number) => void
  toDateTimeLocalValue: (iso: string | null) => string
  fromDateTimeLocalValue: (v: string) => string | undefined
  fmtTs: (isoOrDate: unknown) => string
  active: boolean
  employeeOptions: SuggestOption[]
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round((n / 1024) * 10) / 10} KB`
  return `${Math.round((n / (1024 * 1024)) * 10) / 10} MB`
}

function shortSha(sha: string): string {
  const s = String(sha || '')
  if (s.length <= 12) return s || '—'
  return `${s.slice(0, 12)}…`
}

function uploadLabel(row: FormCompletionRow): string {
  if (row.uploadSucceeded === true) return 'Uploaded'
  if (row.uploadSucceeded === false) return 'Upload failed'
  return 'Not uploaded'
}

export default function FormCompletionsAuditPanel({
  fromTs,
  toTs,
  limit,
  onFromTsChange,
  onToTsChange,
  onLimitChange,
  toDateTimeLocalValue,
  fromDateTimeLocalValue,
  fmtTs,
  active,
  employeeOptions,
}: Props) {
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [rows, setRows] = React.useState<FormCompletionRow[]>([])
  const [formName, setFormName] = React.useState('')
  const [employeeId, setEmployeeId] = React.useState('')
  const [openingId, setOpeningId] = React.useState<string | null>(null)
  const { openDocument } = useDocumentViewer()

  const buildQuery = (): FormCompletionQuery => {
    const q: FormCompletionQuery = { limit }
    if (formName.trim()) q.formName = formName.trim()
    const resolvedEmployeeId = resolveSuggestValue(employeeId, employeeOptions)
    if (resolvedEmployeeId) q.employeeId = resolvedEmployeeId
    const fromIso = fromDateTimeLocalValue(fromTs)
    const toIso = fromDateTimeLocalValue(toTs)
    if (fromIso) q.fromTs = fromIso
    if (toIso) q.toTs = toIso
    return q
  }

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await auditService.listFormCompletions(buildQuery())
      setRows(data)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load form completions')
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    if (!active) return
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active])

  const openPdf = async (row: FormCompletionRow) => {
    const id = String(row._id || '')
    if (!id) return
    const title = row.fileName || row.formName || 'Completed form'
    setOpeningId(id)
    openDocument(title, 'PDF')
    try {
      const { blobUrl, fileName } = await auditService.getFormCompletionPdfBlobUrl(id)
      openDocument(fileName || title, 'PDF', blobUrl)
    } catch {
      openDocument(title, 'PDF', null, 'Failed to load archived form PDF.')
    } finally {
      setOpeningId((current) => (current === id ? null : current))
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm opacity-70">
        Encrypted copies of completed form PDFs, with who completed them and from where.
      </p>

      <div className="rounded-xl border border-base-300 bg-base-200/40 p-4">
        <div className="flex items-center gap-2 mb-3">
          <FontAwesomeIcon icon={faFilter} />
          <span className="font-semibold">Filters</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <div className="form-control flex flex-col">
            <label className="label py-1"><span className="label-text">Form name</span></label>
            <input
              className="input input-bordered"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="jsa"
            />
          </div>
          <div className="form-control flex flex-col">
            <label className="label py-1"><span className="label-text">Employee</span></label>
            <SuggestInput
              value={employeeId}
              onChange={setEmployeeId}
              options={employeeOptions}
              placeholder="Search ID, name, or username…"
              aria-label="Employee"
            />
          </div>
          <div className="form-control flex flex-col">
            <label className="label py-1"><span className="label-text">Limit</span></label>
            <select
              className="select select-bordered"
              value={String(limit)}
              onChange={(e) => onLimitChange(Number(e.target.value))}
            >
              {[25, 50, 100, 200].map((n) => (
                <option key={n} value={String(n)}>{n}</option>
              ))}
            </select>
          </div>
          <div className="form-control flex flex-col">
            <label className="label py-1"><span className="label-text">From</span></label>
            <input
              className="input input-bordered"
              type="datetime-local"
              value={fromTs}
              onChange={(e) => onFromTsChange(e.target.value)}
            />
          </div>
          <div className="form-control flex flex-col">
            <label className="label py-1"><span className="label-text">To</span></label>
            <input
              className="input input-bordered"
              type="datetime-local"
              value={toTs}
              onChange={(e) => onToTsChange(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 justify-end mt-4">
          <button className="btn btn-primary sm:w-40" onClick={() => void load()} disabled={loading}>
            {loading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : <FontAwesomeIcon icon={faMagnifyingGlass} />}
            <span className="ml-2">Apply</span>
          </button>
          <button
            className="btn btn-outline sm:w-40"
            disabled={loading}
            onClick={() => {
              setFormName('')
              setEmployeeId('')
              const now = new Date()
              const from = new Date(now.getTime() - 24 * 60 * 60 * 1000)
              onFromTsChange(toDateTimeLocalValue(from.toISOString()))
              onToTsChange(toDateTimeLocalValue(now.toISOString()))
              onLimitChange(50)
            }}
          >
            <FontAwesomeIcon icon={faRotateLeft} />
            <span className="ml-2">Reset</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm opacity-70">{rows.length} result(s)</div>
        </div>

        <div className="max-h-[min(60vh,36rem)] overflow-auto overscroll-contain rounded-lg border border-base-300">
          <table className="table table-zebra table-sm w-full">
            <thead className="sticky top-0 z-10 bg-base-200 shadow-sm">
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>Form</th>
                <th>IP</th>
                <th>Size</th>
                <th>SHA-256</th>
                <th>Upload</th>
                <th>PDF</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row._id}>
                  <td className="whitespace-nowrap">{fmtTs(row.ts)}</td>
                  <td className="whitespace-nowrap">
                    <div className="flex flex-col">
                      <span className="font-semibold">{row.employeeId || row.username || '—'}</span>
                      <span className="text-xs opacity-70">
                        {row.employeeId
                          ? [row.username, row.accessLevel].filter(Boolean).join(' · ')
                          : (row.accessLevel || '')}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-col">
                      <span className="font-medium">{row.formName}</span>
                      {row.fileName ? <span className="text-xs opacity-70">{row.fileName}</span> : null}
                    </div>
                  </td>
                  <td className="font-mono text-xs">{row.ip || '—'}</td>
                  <td className="whitespace-nowrap">{formatBytes(row.pdfBytes)}</td>
                  <td className="font-mono text-xs" title={row.pdfSha256}>{shortSha(row.pdfSha256)}</td>
                  <td>{uploadLabel(row)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs"
                      disabled={openingId === row._id}
                      onClick={() => {
                        void openPdf(row)
                      }}
                    >
                      {openingId === row._id ? (
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                      ) : (
                        'View PDF'
                      )}
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-8 opacity-70">No form completions for these filters.</td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={8} className="text-center py-8">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
